import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { useSession } from "@/session/SessionProvider";
import { fullName } from "@/session/types";
import {
  fetchSuperAdminData,
  updateCompteRecord,
  updateCompteStatut,
  validerCompteProprietaire,
} from "@/store/platform/superAdminData";
import {
  type AuditActionKey,
  type AuditEntry,
  type Compte,
  type CompteInput,
  type CompteStatut,
  type CompteType,
} from "./types";

interface SuperAdminContextValue {
  comptes: Compte[];
  audit: AuditEntry[];
  administrateur: string;
  chargement: boolean;
  erreur: string | null;
  recharger: () => Promise<void>;
  updateCompte: (id: string, input: CompteInput) => void;
  changeStatut: (id: string, statut: CompteStatut, actionKey: AuditActionKey) => void;
  getCompte: (id: string) => Compte | undefined;
  comptesByType: (type: CompteType) => Compte[];
  auditFor: (compteId: string) => AuditEntry[];
}

const SuperAdminContext = createContext<SuperAdminContextValue | null>(null);

/**
 * Espace Super Admin — branché sur les données RÉELLES de Supabase.
 *
 * - Lecture : table `comptes` (+ rattachements réels : agences, propriétaires,
 *   immeubles, biens, locataires, contrats, employés, abonnements) et
 *   `journal_audit`.
 * - Écriture : mise à jour du statut / de la fiche dans `comptes`, tracée dans
 *   `journal_audit`. En cas d'échec (RLS, contrainte…), la modification
 *   optimiste est annulée et une erreur est publiée pour l'utilisateur.
 *
 * Aucune donnée fictive : les listes sont vides tant que la base l'est.
 */
export function SuperAdminProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const administrateurNom = session ? fullName(session) : "";
  const [comptes, setComptes] = useState<Compte[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const recharger = useCallback(async () => {
    if (!session || session.role !== "super_admin") {
      setComptes([]);
      setAudit([]);
      return;
    }
    setChargement(true);
    setErreur(null);
    try {
      const snapshot = await fetchSuperAdminData();
      setComptes(snapshot.comptes);
      setAudit(snapshot.audit);
    } catch (caught) {
      const detail = describeSupabaseError(caught);
      setErreur("Le chargement des comptes de la plateforme a échoué.");
      dispatchDataError({
        scope: "Super Admin",
        message: "Impossible de charger les comptes de la plateforme.",
        technical: detail,
      });
      console.error("[Super Admin] chargement Supabase échoué", caught);
    } finally {
      setChargement(false);
    }
  }, [session]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  const updateCompte = useCallback(
    (id: string, input: CompteInput) => {
      const previous = comptes.find((compte) => compte.id === id);
      setComptes((prev) =>
        prev.map((compte) => (compte.id === id ? { ...compte, ...input, derniereActivite: new Date().toISOString() } : compte))
      );
      void updateCompteRecord(id, input, administrateurNom)
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setComptes((prev) => prev.map((compte) => (compte.id === id ? previous : compte)));
          dispatchDataError({
            scope: "Super Admin",
            message: "La modification du compte a échoué.",
            technical: describeSupabaseError(caught),
          });
          // L'écriture a pu échouer partiellement : on resynchronise l'état réel.
          void recharger();
        });
    },
    [comptes, administrateurNom, recharger]
  );

  const changeStatut = useCallback(
    (id: string, statut: CompteStatut, actionKey: AuditActionKey) => {
      const previous = comptes.find((compte) => compte.id === id);
      setComptes((prev) =>
        prev.map((compte) => (compte.id === id ? { ...compte, statut, derniereActivite: new Date().toISOString() } : compte))
      );
      // Validation d'un PROPRIÉTAIRE : passage par la RPC serveur qui active le
      // compte ET applique l'offre de lancement (essai 6 mois) une seule fois.
      // Les autres comptes (agences, employés…) gardent le chemin inchangé.
      const viaRpcProprietaire = statut === "actif" && previous?.type === "proprietaire";
      const operation = viaRpcProprietaire
        ? validerCompteProprietaire(id)
        : updateCompteStatut(id, statut, actionKey, administrateurNom);
      void operation
        .then(() => recharger())
        .catch((caught) => {
          if (previous) setComptes((prev) => prev.map((compte) => (compte.id === id ? previous : compte)));
          dispatchDataError({
            scope: "Super Admin",
            message: "Le changement de statut du compte a échoué.",
            technical: describeSupabaseError(caught),
          });
          // Le statut a pu être appliqué en base malgré l'échec de journalisation :
          // on relit la vérité côté serveur pour ne jamais afficher un état faux.
          void recharger();
        });
    },
    [comptes, administrateurNom, recharger]
  );

  const getCompte = useCallback((id: string) => comptes.find((compte) => compte.id === id), [comptes]);

  const comptesByType = useCallback(
    (type: CompteType) => comptes.filter((compte) => compte.type === type),
    [comptes]
  );

  const auditFor = useCallback((compteId: string) => audit.filter((entry) => entry.compteId === compteId), [audit]);

  const value = useMemo(
    () => ({
      comptes,
      audit,
      administrateur: administrateurNom,
      chargement,
      erreur,
      recharger,
      updateCompte,
      changeStatut,
      getCompte,
      comptesByType,
      auditFor,
    }),
    [
      comptes,
      audit,
      administrateurNom,
      chargement,
      erreur,
      recharger,
      updateCompte,
      changeStatut,
      getCompte,
      comptesByType,
      auditFor,
    ]
  );

  return <SuperAdminContext.Provider value={value}>{children}</SuperAdminContext.Provider>;
}

export function useSuperAdmin(): SuperAdminContextValue {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error("useSuperAdmin doit être utilisé à l'intérieur de SuperAdminProvider");
  }
  return context;
}